import { HttpErrorResponse, HttpEventType, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Article } from '../_interfaces/article';
import { Attachment } from '../_interfaces/Attachment';
import { HttpFunctionResult } from '../_interfaces/HttpFunctionResult';
import { FeedbackService, MsgType } from './feedback.service';
import { HttpService } from './http.service';
import { StorageService } from './storage.service';
import { TicketEmployee } from './tickets/ticket.service';
import { Directory, DownloadFileOptions, Filesystem, PermissionStatus } from "@capacitor/filesystem"
import { Share } from '@capacitor/share';
import { Http, HttpDownloadFileOptions, HttpDownloadFileResult } from '@capacitor-community/http';
import * as FileSaver from 'file-saver';
import { ImageViewPage } from '../_pages/image-view/image-view.page';
import { Platform } from '@ionic/angular';
import { PdfViewerPage } from '../_pages/pdf-viewer/pdf-viewer.page';
import { TicketServiceBase } from './tickets/ticketBase.service';
import { TicketCustomer } from './tickets/ticketCustumer.service';
import { AuthService, ROLE } from './auth.service';
import { NativeSettings, AndroidSettings } from 'capacitor-native-settings';

@Injectable({
  providedIn: 'root'
})
export class FilesService {

  private ticketSrv: TicketServiceBase

  constructor(
    private http: HttpService,
    private ticketSrvEmployee: TicketEmployee.TicketService,
    private ticketSrvCustomer: TicketCustomer.TicketService,
    private storageSrv: StorageService,
    private authSrv: AuthService,
    private feedback: FeedbackService,
    private platform: Platform
  ) {
    this.ticketSrv = this.authSrv.role == ROLE.employee ? this.ticketSrvEmployee : this.ticketSrvCustomer
  }

  
  private _uploadProgress : number = 0;
  public get uploadProgress() : number {
    return this._uploadProgress;
  }
  public set uploadProgress(val: number){
    this._uploadProgress = val
  }

  
  private _downloadedFiles : string[] = [];
  public get downloadedFiles() : string[] {
    return this._downloadedFiles;
  }
  public set downloadedFiles(v : string[]) {
    this._downloadedFiles = v;
  }

  public async checkAndRequestPermissions(): Promise<boolean> {
    if(!this.platform.is("android")) return true
    const permissions = await Filesystem.checkPermissions();
    if (permissions.publicStorage === 'denied') {
      const result = await Filesystem.requestPermissions();
      if (result.publicStorage === 'denied') {
        NativeSettings.openAndroid({option: AndroidSettings.ApplicationDetails})
        return false
      }
    }
    return true
  }
  

  public uploadFile(file: File): Promise<HttpFunctionResult<number>>{
    return new Promise(resolve=>{
      if(!file){
        resolve({success: false})
        return
      }
      let formData: FormData = new FormData()
      formData.append("file", file, btoa(unescape(encodeURIComponent(file.name))))
      this.http.UPLOAD(`ticket/file?ticketid=${this.ticketSrv.ticket.id}&workstepid=${this.ticketSrv.activeWorkstepId}` , formData).subscribe((result: any)=>{
        if(result.type === HttpEventType.UploadProgress)  this._uploadProgress = Math.round(100 * result.loaded / result.total)
        else if(result.type === HttpEventType.Response){
          resolve({
            success: true,
            data: result.body.data as number
          })
        }
      }, async (error: HttpErrorResponse)=>resolve(await this.http.handleError(error)))
    })
  }

  public async downloadFile(am: Attachment){

    //neuen token holen damit er nicht abgelaufen ist
    // await this.feedback.updateLoading("Laden...")
    this.http.getNewAccessToken().subscribe(async (result: HttpResponse<any>)=>{
      const accessToken: string = result.body.data
      this.http.token = accessToken
      this.storageSrv.save('jwt-token', accessToken)
      await this.checkAndOpenFile(am)
    }, async (err: HttpErrorResponse)=> {
      await this.feedback.hideLoading()
      await this.http.handleError(err)
    })
  }

  /**
   * Löscht eine Datei vom Server und lokal.
   * @param file - Die zu löschende Datei.
   * @returns Ein Promise, das ein HttpFunctionResult-Objekt mit dem Ergebnis der Anfrage zurückgibt.
   */
  public async deleteFile(file: Attachment): Promise<HttpFunctionResult<any>>{
    const result: HttpFunctionResult<any> = await this.http.DELETE<any>({path: `ticket/attachment/${file.id}/${this.ticketSrv.ticket.id}`})
    if(result.success) await this.deleteFileLocally(file)
    return result
  }

  /**
   * Löscht eine Datei lokal auf dem Gerät.
   * @param am - Die Anlage, die gelöscht werden soll.
   * @returns Eine Promise, die aufgelöst wird, wenn die Datei erfolgreich gelöscht wurde, oder abgelehnt wird, wenn ein Fehler aufgetreten ist.
   */
  public async deleteFileLocally(am: Attachment){
    const filesystemName: string = `${am.id}.${FilesService.getFileExtention(am.description)}`
    if(this.downloadedFiles.find(filename => filename == filesystemName)) await Filesystem.deleteFile({
      path: `attachments/${filesystemName}`,
      directory: Directory.Library
    }).catch(async (e) =>await this.feedback.showError(JSON.stringify(e)))
  }


  /**
   * Speichert einen Artikel in einem Ticket-Workstep als Anhang.
   * @param article Der Artikel, der gespeichert werden soll.
   * @returns Ein Promise, das die ID des gespeicherten Artikels enthält.
   */
  public async saveArticleToTicket(article: Article): Promise<HttpFunctionResult<number>>{
    return await this.http.POST<Article, number>({
      path: `ticket/${this.ticketSrv.ticket.id}/worksteps/${this.ticketSrv.activeWorkstepId}/attachment/article`,
      body: article
    })
  }

  /**
   * Überprüft, ob eine Datei bereits heruntergeladen wurde und öffnet sie gegebenenfalls.
   * Wenn die Datei noch nicht heruntergeladen wurde, wird sie heruntergeladen und dann geöffnet.
   * @param attachment - Die Anlage, die geöffnet werden soll.
   * @returns Eine Promise, die aufgelöst wird, wenn die Datei geöffnet wurde.
   */
  public async checkAndOpenFile(attachment: Attachment){

    //Dateiname in Filesystem
    const filesystemName: string = `${attachment.id}.${FilesService.getFileExtention(attachment.description)}`

    //schauen ob datei da ist -> wenn ja öffnen
    if(this.downloadedFiles.find(attachmentName => attachmentName == filesystemName)) await this.showFile(attachment.description,(await Filesystem.stat({
        path: `attachments/${filesystemName}`,
        directory: Directory.Library
      })).uri, filesystemName) 
    else {
      //Datei downloaden
      await this.feedback.updateLoading("Herunterladen...")
      const url: string = `${this.http.getbaseUrl}ticket/attachment/${attachment.id}?token=${this.http.token}`
      const options: DownloadFileOptions = {
        url: url,
        path:  `attachments/${filesystemName}` ,
        directory: Directory.Library,
        recursive: true,
        method: 'GET',
      }; 
      let response: HttpDownloadFileResult
      try { 
        if(await this.checkAndRequestPermissions()) {
          response = await Filesystem.downloadFile(options)
          await this.feedback.hideLoading()
        } 
        else return await this.feedback.hideLoading()
      } catch (error) {
        console.error(error)
        await this.feedback.showError(JSON.stringify(error))
        await this.feedback.hideLoading( )
      }
      
      if(attachment.type != "Mailanhang") {
        if(this.platform.is("capacitor") && response?.path){
          this._downloadedFiles.push(filesystemName)
          this.ticketSrv.ticket.attachments[this.ticketSrv.ticket.attachments.indexOf(attachment)] = Object.assign({}, attachment)
          await this.showFile(attachment.description, response.path, filesystemName, false, url)
        }else if(response?.blob){
          await this.feedback.hideLoading()
          await this.showFile(attachment.description, URL.createObjectURL(response.blob), null, true, url )
        }
      }else FileSaver.saveAs(URL.createObjectURL(response.blob), `${attachment.id}.msg`)
    } 
    await this.feedback.hideLoading() 
    
  }

  private async showFile(filename: string, path: string, filesystemName: string = null, isWeb: boolean = false, externalUrl: string = ""){

    switch(FilesService.getFileExtention(filename).toLowerCase()){
      case "jpg": case "jpeg": case "png": case "gif": case "bmp": case "webp": case "heic":
        await this.feedback.showModal({
          component: ImageViewPage,
          componentProps: {
            externalUrl: `${externalUrl}&inline=true`,
            imagePath: path,
            filename: filename,
            isWeb: isWeb
          },
          cssClass: "modal-fullscreen",
        })
        break 
      case "pdf":
        const data = !isWeb ? await Filesystem.readFile({
          path: `attachments/${filesystemName}`,
          directory: Directory.Library
        }) : {data: path}
        await this.feedback.showModal({
          component: PdfViewerPage,
          componentProps: {
            externalUrl: `${externalUrl}&inline=true`,
            pdfSrc: isWeb ? data.data : `data:application/pdf;base64,${data.data}`,
            fileName: filename,
            pfdFilesystemPath: path,
            isWeb: isWeb
          },
          cssClass: "modal-fullscreen",
        })
        break
      default:
      if(isWeb){
        FileSaver.saveAs(path, filename)
        return
      }

      await Share.share({
        title: filename,
        files: [path]
      }).catch(async ()=> await this.feedback.hideLoading())
 
    } 
  }  

  public async deleteAllLocalFiles(){
    this._downloadedFiles.forEach(async (file)=>{
      await Filesystem.deleteFile({
        path: `attachments/${file}`,
        directory: Directory.Library
      })
    })
    this._downloadedFiles = []
  }   

  public static getFileExtention(filename: string): string {
    return filename.slice((Math.max(0, filename.lastIndexOf(".")) || Infinity) + 1)
  }

  public  async loadLocalAttachments(){

    if(!this.platform.is("capacitor")) return

    if(!(await Filesystem.readdir({
      path: "",
      directory: Directory.Library
    })).files.find(directory => directory.name == "attachments")) await Filesystem.mkdir({
      path: "attachments",
      directory: Directory.Library
    })

    //Dateinamen aus attachmentsdirectory holen
    this.downloadedFiles = (await Filesystem.readdir({
      path: "attachments",
      directory: Directory.Library
    })).files.map(file => file.name)

  }

  /**
   * Erstellt einen Ticketrport für Web am Server
   * @param {string} fileName Name der PDF für den Ticketreports
   * @param {string} htmlBody Inhalt als HTML des Ticketreports
   * @returns 
   */
  public async createTicketReportOnWeb(fileName: string, htmlBody: string): Promise<HttpFunctionResult<number>>{
    return await this.http.POST<{ticketId: number, workstepId: number, fileName: string, html: string}, number>({
      path: "ticket/file/ticketreport",
      body: {
        ticketId: this.ticketSrv.ticket.id, 
        workstepId: this.ticketSrv.activeWorkstepId,
        fileName: fileName,
        html: htmlBody
      }
    })
  }
}
 